/** The monthly Workflow Products (Book Writing Toolbox, Seller Studio, Legal Production Desk): one plan each, $29.99 a month, 7-day trial. */
export const TRIAL_DAYS = 7;

export type WorkflowSubscriptionId = "book-writing-toolbox" | "seller-studio" | "legal-production-desk";

export interface WorkflowSubscriptionInfo { name: string; monthlyUsd: string; tagline: string; features: string[] }

export const WORKFLOW_SUBSCRIPTIONS: Record<WorkflowSubscriptionId, WorkflowSubscriptionInfo> = {
  "book-writing-toolbox": {
    name: "Book Writing Toolbox",
    monthlyUsd: "29.99",
    tagline: "Write your book and get it ready to publish.",
    features: [
      "Clean, watermark-free book PDF, EPUB and manuscript (.docx and .pdf) downloads, as many as you like",
      "Real KDP trim sizes and professional typesetting, with platform checks before you submit",
      "EPUB export built to WCAG AA accessibility, and ONIX metadata",
      "Previews are free and watermarked for everyone; a subscription removes the watermark",
    ],
  },
  "seller-studio": {
    name: "Seller Studio",
    monthlyUsd: "29.99",
    tagline: "Take every product from photos to a ready listing.",
    features: [
      "A launch board for the whole shop: title, tags, photos, price and prep steps for every product",
      "Photo check: size and shape of every photo against marketplace guidelines, with the count filled in for you",
      "Resize every product's photos for Etsy, eBay, Amazon or your own size in one ZIP",
      "Shop-wide audit: repeated tags, repeated title openings and products that lose money",
      "Price finder: the lowest price that still gives the margin you want, from your own fees",
      "Whole-shop sheet to CSV or Excel, and a backup file you can restore any time",
    ],
  },
  "legal-production-desk": {
    name: "Legal Production Desk",
    monthlyUsd: "29.99",
    tagline: "Prepare a document production from first list to final ZIP.",
    features: [
      "Running Bates ranges for every produced and redacted document, with warnings for gaps and mistakes",
      "One button stamps every PDF and builds the production folder as a ZIP",
      "Production log, withheld list and a privilege log with date, author, recipients and description",
      "A cover letter filled in from your list",
      "Links to OCR, redaction, merge, exhibit labels and bookmarks for the earlier steps",
      "Back up each matter as a file and restore it later",
    ],
  },
};

/** A single tool that does one step of a product, with trial uses to try before paying for the whole product. */
export interface TryFirstLink { toolId: string; label: string }
export interface WorkflowPreview { deliverables: string[]; tryFirst: TryFirstLink[] }

/**
 * What a person actually receives from each product, taken from the files the code writes (LegalProductionDesk.tsx,
 * SellerStudio.tsx). Shown on the paywall so a visitor can judge the product before signing in. No screenshots: none are faked.
 */
export const WORKFLOW_PREVIEWS: Record<WorkflowSubscriptionId, WorkflowPreview> = {
  "book-writing-toolbox": {
    deliverables: [
      "<title>.pdf: the typeset book interior and cover, without the preview watermark",
      "<title>.epub: an EPUB 3 ebook",
      "Manuscript in Standard Manuscript Format as .docx and .pdf",
    ],
    tryFirst: [
      { toolId: "epub-maker", label: "EPUB Maker: build one EPUB (Premium Application)" },
      { toolId: "book-interior-maker", label: "Book Interior Maker: typeset one interior (Premium Application)" },
    ],
  },
  "legal-production-desk": {
    deliverables: [
      "<matter>-production.zip, holding a production folder of every PDF stamped with its running Bates number",
      "production-log.csv: each document with its start and end Bates number and page count",
      "withheld-documents.csv: the documents you held back",
      "privilege-log.csv: date, author, recipients and description for withheld and redacted documents",
      "cover-letter.txt, filled in from your list",
      "Each log can also be saved on its own as CSV or Excel, and a matter can be backed up as a file",
    ],
    tryFirst: [{ toolId: "bates-numbering", label: "Bates Numbering: stamp one set of PDFs and get the index CSV" }],
  },
  "seller-studio": {
    deliverables: [
      "launch-board.csv or launch-board.xlsx: one row per product with title, tags, price, costs, photo checks and prep steps",
      "photos-<size>.zip: every product's photos resized for Etsy, eBay, Amazon or your own size",
      "On-screen shop audit (repeated tags, repeated title openings, products that lose money) and a price finder from your own fees",
      "A backup file of the whole board that you can restore later (photos are not part of it)",
    ],
    tryFirst: [
      { toolId: "etsy-listing-checker", label: "Etsy Listing Checker: check one title and its tags" },
      { toolId: "listing-photo-pack", label: "Listing Photo Pack: resize one product's photos" },
      { toolId: "marketplace-fee-calculator", label: "Marketplace Fee Calculator: see the profit on one sale" },
    ],
  },
};

/** The step-one tools shown on the Event Suite page. */
export const EVENT_SUITE_TRY_FIRST: TryFirstLink[] = [
  { toolId: "guest-list-tracker", label: "Guest List Tracker: build one guest list" },
  { toolId: "event-budget-planner", label: "Event Budget Planner: plan one budget" },
  { toolId: "agenda-builder", label: "Agenda Builder: print one programme" },
];
